import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { Text } from "@/components/podkit/typography/Text";
import { useRunnerEnvironments } from "@/queries/environment-queries";
import { useDeleteRunner, type PlainRunner } from "@/queries/runner-queries";
import { AWSRunnerAdditionalFieldsKeys } from "@/components/runners/details/runner-configuration-keys";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { CopyableTextArea } from "@/components/podkit/forms/CopyableTextArea";

type DeleteRunnerModalProps = {
    runner: PlainRunner;
    open: boolean;
    onClose: () => void;
    onDeleteSuccessful?: () => void;
};

export const DeleteRunnerModal: FC<DeleteRunnerModalProps> = ({
    open,
    runner,
    onClose,
    onDeleteSuccessful,
}: DeleteRunnerModalProps) => {
    const { toast } = useToast();

    const deleteRunner = useDeleteRunner();
    const listEnvironments = useRunnerEnvironments(runner.runnerId);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteRunner = useCallback(() => {
        deleteRunner.mutate(
            {
                runnerId: runner.runnerId,
                force: false,
            },
            {
                onSuccess: () => {
                    toast({ title: "Runner scheduled for deletion" });
                    onClose();
                    if (onDeleteSuccessful) {
                        onDeleteSuccessful();
                    }
                },
                onError: (err) => {
                    toast({ title: "Failed to delete runner", description: formatError(err) });
                },
            },
        );
    }, [deleteRunner, onClose, onDeleteSuccessful, runner.runnerId, toast]);

    const environments = listEnvironments.data?.environments || [];
    const activeEnvironments = environments.filter((env) => env.status?.phase === EnvironmentPhase.RUNNING);
    const stoppedEnvironments = environments.filter((env) => env.status?.phase !== EnvironmentPhase.RUNNING);
    const totalEnvironments = environments.length;
    const stackURL = runner.status?.additionalInfo.find(
        (info) => info.key === AWSRunnerAdditionalFieldsKeys.StackURL,
    )?.value;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.DeleteRunnerModal}>
                <DialogHeader>
                    <DialogTitle>Delete runner</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <div className="flex flex-col space-y-4">
                        {totalEnvironments > 0 ? (
                            <>
                                <Text>All environments associated with this runner will also be deleted.</Text>
                                <Text className="font-bold">
                                    This will affect {totalEnvironments} environment
                                    <span>{totalEnvironments > 1 ? "s" : ""}</span>:
                                    <ul className="mt-2 list-inside list-disc space-y-1 font-normal">
                                        <li>{activeEnvironments.length} running</li>
                                        <li>{stoppedEnvironments.length} stopped</li>
                                    </ul>
                                </Text>
                            </>
                        ) : (
                            <Text>There are no environments associated with this runner.</Text>
                        )}
                        <Text>Are you sure you&apos;d like to delete it?</Text>

                        {/* Show provider-specific cleanup instructions */}
                        <div className="space-y-3 rounded-md bg-surface-tertiary p-4">
                            <Text className="font-bold">Important: Required cleanup steps</Text>

                            {runner.provider === RunnerProvider.AWS_EC2 && stackURL && (
                                <>
                                    <Text>
                                        Remember to delete the associated AWS CloudFormation stack manually after this
                                        operation.
                                    </Text>
                                    <Text className="text-sm">
                                        Stack URL:{" "}
                                        <ExternalLink href={stackURL} className="break-all">
                                            {stackURL}
                                        </ExternalLink>
                                    </Text>
                                </>
                            )}

                            {runner.provider === RunnerProvider.LINUX_HOST && (
                                <div className="flex flex-col gap-4">
                                    <Text>
                                        After deleting the runner from Gitpod, complete these steps on your Linux machine:
                                    </Text>

                                    <div className="flex flex-col gap-3">
                                        <Text className="font-medium">1. Stop the runner process</Text>
                                        <div className="text-sm text-content-secondary">
                                            If running as a systemd service:
                                        </div>
                                        <CopyableInput
                                            className="text-sm font-mono w-full max-w-none"
                                            copyable
                                            value="sudo systemctl stop gitpod-runner"
                                        />
                                        <div className="text-sm text-content-secondary">
                                            If running in the terminal, press Ctrl+C or terminate the <code>gitpod runner run</code> process
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <Text className="font-medium">
                                            2. Remove runner files and environment data
                                        </Text>
                                        <div className="text-sm text-content-secondary">
                                            This will delete all runner files and environment data
                                        </div>
                                        <CopyableInput
                                            className="text-sm font-mono w-full max-w-none"
                                            copyable
                                            value="rm -rf ~/gitpod-runner"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <Text className="font-medium">3. (Optional) Clean up service files</Text>
                                        <div className="text-sm text-content-secondary">
                                            If you set up a systemd service, you can remove it with:
                                        </div>
                                        <CopyableTextArea
                                            className="text-sm font-mono w-full max-w-none"
                                            copyable
                                            value={`sudo systemctl disable gitpod-runner
sudo rm /etc/systemd/system/gitpod-runner.service`}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="sm:justify-end">
                    <DialogClose asChild>
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteRunner.isPending}
                        onClick={handleDeleteRunner}
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const ForceDeleteRunnerModal: FC<DeleteRunnerModalProps> = ({
    open,
    runner,
    onClose,
}: DeleteRunnerModalProps) => {
    const { toast } = useToast();

    const deleteRunner = useDeleteRunner();
    const listEnvironments = useRunnerEnvironments(runner.runnerId);
    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteRunner = useCallback(() => {
        deleteRunner.mutate(
            { runnerId: runner.runnerId, force: true },
            {
                onSuccess: () => {
                    toast({ title: "Runner force deleted" });
                    onClose();
                },
                onError: (err) => {
                    toast({ title: "Failed to force delete runner", description: formatError(err) });
                },
            },
        );
    }, [deleteRunner, onClose, runner, toast]);

    let withUndeletedText = `${listEnvironments.data?.environments.length} undeleted environments`;
    if (listEnvironments.data?.environments.length === 0) {
        withUndeletedText = "no undeleted environments";
    } else if (listEnvironments.data?.environments.length === 1) {
        withUndeletedText = "1 undeleted environment";
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.ForceDeleteRunnerModal}>
                <DialogHeader>
                    <DialogTitle>Warning: Irreversible Action</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <Text>
                        You are about to permanently and forcibly delete{" "}
                        <span className="text-lg font-bold text-content-primary">&quot;{runner.name}&quot;</span>{" "}
                        {!listEnvironments.isLoading && (
                            <span>
                                with <strong>{withUndeletedText}</strong>.{" "}
                            </span>
                        )}
                        <span>However, please note:</span>
                    </Text>

                    <ul className="list-outside list-disc py-4 pl-6">
                        <li className="pl-1">
                            <span className="text-lg font-bold">Infrastructure and Files</span>: This action does not
                            guarantee the cleanup of any related infrastructure, files, or other dependencies which may
                            continue to exist.
                        </li>
                        <li className="pl-1">
                            <span className="text-lg font-bold">Manual Cleanup Required</span>: You may need to manually
                            verify and clean up these elements.
                        </li>
                    </ul>

                    <Text>Are you sure you want to proceed with Force Delete?</Text>
                </DialogBody>

                <DialogFooter className="sm:justify-end">
                    <DialogClose asChild>
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteRunner.isPending}
                        onClick={handleDeleteRunner}
                    >
                        Yes, Force Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
